import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from 'firebaseConfig';
import { getCurrenSem } from 'utils/methods/math';

function sanitizePathSegment(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9_-]/g, '');
}

export function getAssignmentReplaySemesterPathSegments(parentOrgId = null) {
    const semKey = getCurrenSem(parentOrgId);
    const parts = String(semKey || '').split(' - ');
    const semesterRaw = parts[0] || 'semester 1';
    const yearRaw = parts[1] || dayjs().format('YYYY');
    return {
        year: sanitizePathSegment(yearRaw),
        semester: sanitizePathSegment(semesterRaw),
        semKey,
    };
}

export function buildAssignmentSessionReplayStoragePath(
    submissionId,
    sessionId,
    chunkIndex,
    parentOrgId = null
) {
    const { year, semester } = getAssignmentReplaySemesterPathSegments(parentOrgId);
    const sub = String(submissionId || 'unknown').trim();
    const sid = String(sessionId || 'unknown').trim();
    const idx = Math.max(0, Math.floor(Number(chunkIndex) || 0));
    return `assignment-session-replays/${year}/${semester}/${sub}/${sid}/chunk-${idx}.json`;
}

export async function uploadAssignmentSessionReplayJson(
    submissionId,
    sessionId,
    chunkIndex,
    jsonPayload,
    parentOrgId = null
) {
    const path = buildAssignmentSessionReplayStoragePath(
        submissionId,
        sessionId,
        chunkIndex,
        parentOrgId
    );
    const { year, semester, semKey } = getAssignmentReplaySemesterPathSegments(parentOrgId);
    const body = JSON.stringify({
        ...jsonPayload,
        storage_year: year,
        storage_semester: semester,
        storage_semester_key: semKey,
    });
    const blob = new Blob([body], { type: 'application/json' });
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, blob);
    const url = await getDownloadURL(fileRef);
    return { url, byte_size: blob.size, path };
}

export async function uploadAssignmentSessionReplayWithRetry(
    submissionId,
    sessionId,
    chunkIndex,
    jsonPayload,
    parentOrgId = null,
    maxAttempts = 2
) {
    let lastError = null;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
            return await uploadAssignmentSessionReplayJson(
                submissionId,
                sessionId,
                chunkIndex,
                jsonPayload,
                parentOrgId
            );
        } catch (error) {
            lastError = error;
            Sentry.captureException(error);
        }
    }
    throw lastError;
}
